import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import cn from 'classnames';

const SwipeButton = ({
  onComplete,
  text = 'Swipe to open',
  className = '',
  disabled = false,
  icon = 'lucide:arrow-right',
  completeText = 'Complete!',
  resetAfterComplete = true,
  resetDelay = 1000,
  completed: externalCompleted = false, // New external completed prop
  loadingText = 'Processing...' // Text to show while waiting for external completion
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(0);
  const [swiped, setSwiped] = useState(false); // Track if swiped but waiting for external completion
  const [internalCompleted, setInternalCompleted] = useState(false);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  const maxTravel = useRef(0);

  const padding = 8; // Padding on both sides for the thumb

  // Determine if the component is fully completed based on both swiped state and external completed prop
  const isCompleted = internalCompleted && externalCompleted;

  useEffect(() => {
    const updateMaxTravel = () => {
      if (containerRef.current && buttonRef.current) {
        maxTravel.current =
          containerRef.current.offsetWidth -
          buttonRef.current.offsetWidth -
          padding * 2;
      }
    };

    updateMaxTravel();
    window.addEventListener('resize', updateMaxTravel);
    return () => window.removeEventListener('resize', updateMaxTravel);
  }, []);

  // Watch for changes in the external completed prop
  useEffect(() => {
    if (swiped && externalCompleted && !internalCompleted) {
      setInternalCompleted(true);
    }
  }, [externalCompleted, swiped, internalCompleted]);

  const resetButton = () => {
    setInternalCompleted(false);
    setSwiped(false);
    setPosition(0);
  };

  useEffect(() => {
    // Only start the reset timer when externalCompleted becomes true
    if (swiped && externalCompleted && resetAfterComplete) {
      const resetTimer = setTimeout(() => {
        resetButton();
      }, resetDelay);

      return () => clearTimeout(resetTimer);
    }
  }, [swiped, externalCompleted, resetAfterComplete, resetDelay]);

  const handleStart = () => {
    if (!disabled && !swiped && !internalCompleted) {
      setIsDragging(true);
    }
  };

  const handleMove = (clientX) => {
    if (isDragging && !disabled && !swiped && !internalCompleted) {
      const rect = containerRef.current.getBoundingClientRect();
      const thumbRect = buttonRef.current.getBoundingClientRect();
      const newPosition = Math.max(
        0,
        Math.min(clientX - rect.left - thumbRect.width / 2 - padding, maxTravel.current)
      );
      setPosition(newPosition);

      if (newPosition >= maxTravel.current * 0.9) {
        setSwiped(true);
        setPosition(maxTravel.current);
        setIsDragging(false);
        
        // Only set internal completed if external completed is also true
        if (externalCompleted) {
          setInternalCompleted(true);
        }
        
        if (onComplete) onComplete(resetButton);
      }
    }
  };

  const handleEnd = () => {
    if (isDragging && !swiped) {
      setIsDragging(false);
      setPosition(0);
    }
  };

  const handleTouchStart = (e) => handleStart();
  const handleTouchMove = (e) => handleMove(e.touches[0].clientX);
  const handleTouchEnd = () => handleEnd();

  const handleMouseDown = (e) => handleStart();
  const handleMouseMove = (e) => {
    if (isDragging) {
      handleMove(e.clientX);
    }
  };
  const handleMouseUp = () => handleEnd();

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const progressPercentage = (position / maxTravel.current) * 100;

  // Determine what text to display based on component state
  // Determine what text to display based on component state
  const displayText = isCompleted 
    ? completeText 
    : swiped 
      ? loadingText 
      : text;

  return (
    <div
      className={cn(
        'relative h-16 rounded-2xl overflow-hidden select-none focus:outline-none',
        {
          'bg-black': !disabled,
          'bg-black/30': disabled,
          'cursor-not-allowed': disabled,
          'cursor-pointer': !disabled
        },
        className
      )}
      ref={containerRef}
      aria-disabled={disabled}
    >
      <div
        className={cn(
          'absolute inset-y-0 left-0 transition-opacity',
          {
            'bg-dark': !disabled,
            'bg-gray-400': disabled,
            'opacity-70': !isCompleted,
            'opacity-100': isCompleted
          }
        )}
        style={{
          width: !disabled ? `calc(${progressPercentage}% + ${padding}px)` : ''
        }}
      />
      <div className={cn(
        'absolute inset-0 flex items-center justify-center font-medium',
        {
          'text-white': !disabled,
          'text-black/40': disabled
        }
      )}>
        {displayText}
      </div>

      <div
        ref={buttonRef}
        className={cn(
          'absolute top-2 bottom-2 aspect-square rounded-xl shadow-lg flex items-center justify-center transition-transform',
          {
            'bg-white': !disabled,
            'bg-gray-200': disabled
          }
        )}
        style={{
          transform: `translateX(${position}px)`,
          touchAction: 'none',
          left: `${padding}px`
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Icon
          icon={isCompleted ? 'lucide:check' : swiped ? 'lucide:loader' : icon}
          width={24}
          className={cn({
            'text-green-500': isCompleted && !disabled,
            'text-blue-500': swiped && !isCompleted && !disabled,
            'text-dark': !swiped && !isCompleted && !disabled,
            'text-gray-400': disabled,
            'animate-spin': swiped && !isCompleted
          })}
        />
      </div>
    </div>
  );
};

export default SwipeButton;